import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Popover,
  Tooltip
} from '@mui/material';
import { Search } from '@mui/icons-material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HistoryIcon from '@mui/icons-material/History';
import { format } from 'date-fns';
import { useStaleInventory } from '../hooks/useDashboardData';

export const StaleInventoryTable: React.FC = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [days, setDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedProperties, setSelectedProperties] = useState<Record<string, string> | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page when searching
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useStaleInventory(
    days,
    page + 1,
    rowsPerPage,
    debouncedSearchTerm
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenProperties = (event: React.MouseEvent<HTMLElement>, properties: Record<string, string>) => {
    setAnchorEl(event.currentTarget);
    setSelectedProperties(properties);
  };

  const handleCloseProperties = () => {
    setAnchorEl(null);
    setSelectedProperties(null);
  };

  const handleViewHistory = (unitId: string) => {
    // TODO: Navigate to unit history page or open history modal
    console.log('View history for unit:', unitId);
    // Example: navigate(`/units/${unitId}/history`);
  };

  const openPopover = Boolean(anchorEl);

  const getDaysColor = (daysSinceMovement: number) => {
    if (daysSinceMovement > 60) return theme.palette.error.main;
    if (daysSinceMovement > 30) return theme.palette.warning.main;
    return theme.palette.info.main;
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              Stale Inventory
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                value={days}
                onChange={(e) => {
                  setDays(Number(e.target.value));
                  setPage(0);
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value={15}>Last 15 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={999}>More than 90 days</MenuItem>
              </TextField>
            </Box>
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Item Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Symbol
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Quantity
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Warehouse
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Last Movement
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Days Idle
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Properties
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from(new Array(rowsPerPage)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="error">Failed to load data</Typography>
                  </TableCell>
                </TableRow>
              ) : data?.items && data.items.length > 0 ? (
                data.items.map((item) => (
                  <TableRow
                    key={item.unitId}
                    hover
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.itemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.barcode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.symbol}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.totalQuantity}
                        size="small"
                        sx={{
                          backgroundColor: theme.colors.alpha.black[5],
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.warehouseName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.warehouseCode}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {format(new Date(item.lastMovement), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${item.daysSinceMovement} days`}
                        size="small"
                        sx={{
                          backgroundColor: getDaysColor(
                            item.daysSinceMovement
                          ),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {item.additionalProperties && (() => {
                          const propertyEntries = Object.entries(item.additionalProperties);
                          const displayedProperties = propertyEntries.slice(0, 2);
                          const hasMoreProperties = propertyEntries.length > 2;

                          return (
                            <>
                              {displayedProperties.map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                              {hasMoreProperties && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenProperties(e, item.additionalProperties!)}
                                  sx={{ ml: 0.5 }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton>
                              )}
                              {propertyEntries.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                        {!item.additionalProperties && (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Movement History">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewHistory(item.unitId)}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      {searchTerm ? 'No matching items found' : 'No stale inventory found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.totalCount > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50]}
            component="div"
            count={data.totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </CardContent>

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleCloseProperties}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 400 }}>
          <Typography variant="subtitle2" gutterBottom>
            All Properties
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {selectedProperties &&
              Object.entries(selectedProperties).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {key}:
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Box>
              ))}
          </Box>
        </Box>
      </Popover>
    </Card>
  );
};